// Smart Sash Admin Panel - Certification Validators
// Validation functions for certification questions and answers

import {
  CertificationAnswer,
  CertificationQuestionParsed,
} from '@/models/certification.models';
import { ANSWER_IDS, AnswerId } from './certificationConstants';

/**
 * Validation error with field and message
 */
export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Validation result
 */
export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

/**
 * Check if an ID is a valid answer ID (a-h)
 */
export const isValidAnswerId = (id: unknown): id is AnswerId => {
  return typeof id === 'string' && ANSWER_IDS.includes(id as AnswerId);
};

/**
 * Validate a single answer
 */
export const validateAnswer = (
  answer: CertificationAnswer,
  index: number
): ValidationError[] => {
  const errors: ValidationError[] = [];
  const fieldPrefix = `answers[${index}]`;

  if (!answer) {
    errors.push({
      field: fieldPrefix,
      message: 'Answer is required',
    });
    return errors;
  }

  if (!isValidAnswerId(answer.id)) {
    errors.push({
      field: `${fieldPrefix}.id`,
      message: `Invalid answer ID: ${answer.id}. Must be one of: ${ANSWER_IDS.join(', ')}`,
    });
  }

  if (typeof answer.text !== 'string') {
    errors.push({
      field: `${fieldPrefix}.text`,
      message: 'Answer text must be a string',
    });
  }

  if (typeof answer.isCorrect !== 'boolean') {
    errors.push({
      field: `${fieldPrefix}.isCorrect`,
      message: 'isCorrect must be a boolean',
    });
  }

  return errors;
};

/**
 * Validate answers array
 */
export const validateAnswers = (answers: CertificationAnswer[]): ValidationResult => {
  const errors: ValidationError[] = [];

  if (!Array.isArray(answers)) {
    return {
      valid: false,
      errors: [{ field: 'answers', message: 'Answers must be an array' }],
    };
  }

  if (answers.length !== 8) {
    errors.push({
      field: 'answers',
      message: `Expected 8 answers, got ${answers.length}`,
    });
  }

  answers.forEach((answer, index) => {
    errors.push(...validateAnswer(answer, index));
  });

  // Check for duplicate IDs
  const ids = answers.map((a) => a.id);
  const uniqueIds = new Set(ids);
  if (uniqueIds.size !== ids.length) {
    errors.push({
      field: 'answers',
      message: 'Duplicate answer IDs found',
    });
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Validate that correct answer references a valid answer
 */
export const validateCorrectAnswer = (
  correctAnswer: string | null | undefined,
  answers: CertificationAnswer[]
): ValidationResult => {
  const errors: ValidationError[] = [];

  if (!correctAnswer) {
    errors.push({
      field: 'correctAnswer',
      message: 'Correct answer is required',
    });
    return { valid: false, errors };
  }

  if (!isValidAnswerId(correctAnswer)) {
    errors.push({
      field: 'correctAnswer',
      message: `Invalid correct answer ID: ${correctAnswer}`,
    });
  }

  const answerExists = answers.some((a) => a.id === correctAnswer);
  if (!answerExists) {
    errors.push({
      field: 'correctAnswer',
      message: `Correct answer "${correctAnswer}" does not match any answer ID`,
    });
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Full validation of a parsed question
 */
export const validateQuestion = (
  question: CertificationQuestionParsed
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Validate question text
  if (!question.question || question.question.trim() === '') {
    errors.push({
      field: 'question',
      message: 'Question text is required',
    });
  }

  // Validate question type
  if (!question.questionType) {
    errors.push({
      field: 'questionType',
      message: 'Question type is required',
    });
  }

  // Validate points
  if (typeof question.points !== 'number' || question.points < 0) {
    errors.push({
      field: 'points',
      message: 'Points must be a non-negative number',
    });
  }

  // Validate answers array
  const answersResult = validateAnswers(question.answers);
  errors.push(...answersResult.errors);

  // Validate correct answer
  const correctResult = validateCorrectAnswer(question.correctAnswer, question.answers);
  errors.push(...correctResult.errors);

  // Validate that at least one answer has text
  if (!hasAtLeastOneAnswer(question)) {
    errors.push({
      field: 'answers',
      message: 'At least one answer must have text',
    });
  }

  // Validate that correct answer has text
  if (!correctAnswerHasText(question)) {
    errors.push({
      field: 'correctAnswer',
      message: 'The correct answer must have text',
    });
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Lighter validation for saving drafts
 * Only checks critical fields, allows empty answers
 */
export const validateQuestionForSave = (
  question: CertificationQuestionParsed
): ValidationResult => {
  const errors: ValidationError[] = [];

  // Only require institutionId for save
  if (!question.institutionId) {
    errors.push({
      field: 'institutionId',
      message: 'Institution ID is required',
    });
  }

  // Validate answers structure (but allow empty text)
  if (!Array.isArray(question.answers)) {
    errors.push({
      field: 'answers',
      message: 'Answers must be an array',
    });
  }

  // Validate points is a number
  if (typeof question.points !== 'number') {
    errors.push({
      field: 'points',
      message: 'Points must be a number',
    });
  }

  return {
    valid: errors.length === 0,
    errors,
  };
};

/**
 * Check if at least one answer has text
 */
export const hasAtLeastOneAnswer = (question: CertificationQuestionParsed): boolean => {
  return question.answers.some((a) => a.text && a.text.trim() !== '');
};

/**
 * Check if the correct answer has text
 */
export const correctAnswerHasText = (question: CertificationQuestionParsed): boolean => {
  const correctAnswer = question.answers.find((a) => a.id === question.correctAnswer);
  return Boolean(correctAnswer && correctAnswer.text && correctAnswer.text.trim() !== '');
};

/**
 * Get count of answers with text
 */
export const countAnswersWithText = (question: CertificationQuestionParsed): number => {
  return question.answers.filter((a) => a.text && a.text.trim() !== '').length;
};

/**
 * Check if question is ready for publishing (full validation)
 */
export const isQuestionReadyForPublish = (question: CertificationQuestionParsed): boolean => {
  const result = validateQuestion(question);
  return result.valid;
};

/**
 * Get validation summary as a single string
 */
export const getValidationSummary = (result: ValidationResult): string => {
  if (result.valid) {
    return '';
  }
  return result.errors.map((e) => e.message).join('; ');
};